// Mouse, keyboard and touch input for orbit / walk / plan.
import type { Viewer } from './viewer.ts';
import { type V3, add, sub, scale, dist, norm, clamp, forward } from './math.ts';

type Drag = 'orbit' | 'pan' | 'look';
interface P { x: number; y: number; x0: number; y0: number; t0: number; m: number }   // t0: the down event's timeStamp (when it happened, not when handled); m: how far it has moved (px, in all)

export class Controls {
  pivot: V3 | null = null;
  depth = 8;                 // fallback distance when nothing is under the cursor
  /** A click or tap left in place: return true if it was used (a tool, a menu); otherwise it focuses the point there. */
  onTap: (x: number, y: number) => boolean = () => false;
  onMove: () => void = () => {};
  /** Right-click, or a long press (touch, pen) held still: a menu at (x, y). Return true if one opened. */
  onContext: (x: number, y: number) => boolean = () => false;
  joy: [number, number] = [0, 0];
  /** A mouse drag orbiting locks the pointer (hidden, never stopped by the screen's edge), back where it was when let go. */
  lockOrbit = true;
  /**
   * A tool (lasso, brush) that wants this pointer: return true on down to own
   * it until up. A second finger takes it back ('cancel'), so pinch and
   * two-finger pan still work. A mouse never strokes with its buttons (a drag
   * orbits): moving it with ctrl, shift or alt held starts a stroke ('down'
   * from a pointermove, the keys held in it), which runs until they are let go.
   */
  capture: ((e: PointerEvent, phase: 'down' | 'move' | 'up' | 'cancel') => boolean) | null = null;
  /**
   * Where else (outside the canvas) a mouse key stroke may start: elements over the view that let it be seen
   * (the View tab, say). Once started, a stroke follows the mouse over the whole window, panels and all.
   */
  strokeFrom: (target: Element) => boolean = () => false;
  /** While true, a left mouse drag is the tool's stroke (as a touch is) rather than an orbit: an armed lasso. */
  dragStrokes: () => boolean = () => false;
  private captured = new Map<number, P>();
  /** The mouse's key stroke: its last event; null with no stroke. at: where the keys were first seen held, to start from once it moves. */
  private keyStroke: PointerEvent | null = null;
  private keyAt: [number, number] | null = null;
  private ptrs = new Map<number, P>();
  private drag: Drag | null = null;
  private dragDepth = 8;
  private pinch: { d: number; mx: number; my: number; anchor: V3 | null } | null = null;
  private keys = new Set<string>();
  private wheelHit: { x: number; y: number; t: number; p: V3 | null } | null = null;
  private anim: { t0: number; dur: number; e0: V3; e1: V3; y0: number; y1: number; p0: number; p1: number } | null = null;
  private stillTimer = 0;
  /** A finger (or pen) down alone, not yet moved: held long enough, it opens the menu (onContext). */
  private press: { id: number; x0: number; y0: number; timer: number } | null = null;

  constructor(private v: Viewer, private el: HTMLElement) {
    el.addEventListener('pointerdown', e => this.down(e));
    el.addEventListener('pointermove', e => this.move(e));
    el.addEventListener('pointerup', e => this.up(e));
    el.addEventListener('pointercancel', e => this.up(e, true));
    el.addEventListener('wheel', e => this.wheel(e), { passive: false });
    el.addEventListener('contextmenu', e => e.preventDefault());
    for (const t of ['gesturestart', 'gesturechange', 'gestureend']) el.addEventListener(t, e => e.preventDefault());
    addEventListener('keydown', e => {
      if ((e.target as HTMLElement).closest?.('input,select,textarea')) return;
      this.keys.add(e.code);
    });
    addEventListener('keyup', e => {
      this.keys.delete(e.code);
      if (!e.ctrlKey && !e.shiftKey && !e.altKey) {
        if (this.keyStroke && e.key === 'Alt') e.preventDefault();   // not the menu bar
        this.endKeyStroke(false);
      }
    });
    addEventListener('blur', () => { this.keys.clear(); this.endKeyStroke(true); });
    // a mouse moving with no button over something else (a panel): the key stroke goes on there, as if over the
    // canvas (the event sent on to it, so offsetX/Y are the canvas's); one may start there too, where strokeFrom says
    addEventListener('pointermove', e => {
      const t = e.target as Element | null;
      if (t === el || e.pointerType !== 'mouse' || e.buttons || !this.capture || !t?.closest) return;
      if (!this.keyStroke && (!(e.ctrlKey || e.shiftKey || e.altKey) || t.closest('input,select,textarea') || !this.strokeFrom(t))) { if (!this.keyStroke) this.keyAt = null; return; }
      el.dispatchEvent(new PointerEvent('pointermove', e));
    });
    document.addEventListener('mousemove', e => {
      if (document.pointerLockElement !== this.el || this.v.cam.mode !== 'walk') return;
      this.look(-e.movementX * 0.0025, -e.movementY * 0.0025);
    });
  }

  private get cam() { return this.v.cam; }

  /** Mark the camera as moving; the viewer drops resolution until it settles. */
  touched() {
    this.v.moving = true;
    this.v.dirty = true;
    clearTimeout(this.stillTimer);
    this.stillTimer = setTimeout(() => { this.v.moving = false; this.v.dirty = true; this.onMove(); }, 180) as unknown as number;
    this.onMove();
  }

  private pickOr(x: number, y: number): { p: V3; hit: boolean } {
    const h = this.v.pickAt(x, y);
    if (h) {
      this.depth = clamp(this.cam.ortho ? 5 : dist(this.cam.eye, h.p), 0.3, 200);
      return { p: h.p, hit: true };
    }
    const r = this.cam.ray(x, y);
    return { p: add(r.o, scale(r.d, this.depth)), hit: false };
  }

  /** The keys let go (or the window lost focus: cancel): the stroke ends. */
  private endKeyStroke(cancel: boolean) {
    this.keyAt = null;
    const e = this.keyStroke;
    if (!e) return;
    this.keyStroke = null;
    this.capture?.(e, cancel ? 'cancel' : 'up');
  }

  /** The mouse moving with no button down: with ctrl, shift or alt held, a selection stroke. */
  private hover(e: PointerEvent) {
    if (this.keyStroke) {
      if (!e.ctrlKey && !e.shiftKey && !e.altKey) { this.endKeyStroke(false); return; }   // a keyup missed (focus elsewhere)
      this.keyStroke = e;
      this.capture?.(e, 'move');
      return;
    }
    if (!(e.ctrlKey || e.shiftKey || e.altKey) || this.cam.mode === 'walk' || !this.capture) { this.keyAt = null; return; }
    if (!this.keyAt) { this.keyAt = [e.offsetX, e.offsetY]; return; }
    if (Math.hypot(e.offsetX - this.keyAt[0], e.offsetY - this.keyAt[1]) < 4) return;   // a key pressed, not yet a stroke
    if (this.capture(e, 'down')) this.keyStroke = e;
    else this.keyAt = null;
  }

  private down(e: PointerEvent) {
    if (this.keyStroke) return;   // buttons do nothing while the keys draw
    this.el.setPointerCapture(e.pointerId);
    this.endPress();
    if (e.pointerType !== 'mouse' && e.isPrimary && !this.ptrs.size && !this.captured.size) {
      const x = e.offsetX, y = e.offsetY;
      this.press = { id: e.pointerId, x0: x, y0: y, timer: setTimeout(() => this.longPress(e, x, y), 500) as unknown as number };
    }
    const at = (): P => ({ x: e.offsetX, y: e.offsetY, x0: e.offsetX, y0: e.offsetY, t0: e.timeStamp, m: 0 });
    if (this.captured.size) {
      // another finger: drop the tool's stroke, hand its pointer back to navigation
      this.capture?.(e, 'cancel');
      for (const [id, p] of this.captured) this.ptrs.set(id, p);
      this.captured.clear();
    } else if ((e.pointerType !== 'mouse' || (e.button === 0 && this.dragStrokes())) && !this.ptrs.size && e.isPrimary && this.capture?.(e, 'down')) { this.captured.set(e.pointerId, at()); return; }
    this.ptrs.set(e.pointerId, at());
    this.anim = null;
    this.lockAsked = false;
    const mode = this.cam.mode;
    if (this.ptrs.size === 2) {
      const [a, b] = [...this.ptrs.values()];
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      this.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), mx, my, anchor: mode === 'orbit' ? this.pickOr(mx, my).p : null };
      this.drag = null;
      return;
    }
    if (this.ptrs.size > 2) return;
    if (mode === 'walk') {
      if (e.pointerType === 'mouse' && document.pointerLockElement !== this.el) this.el.requestPointerLock?.();
      this.drag = 'look';
    } else if (mode === 'plan') this.drag = 'pan';
    else {
      const panBtn = e.button === 1 || e.button === 2;   // a left drag always orbits (its keys are the selection tools')
      this.drag = panBtn ? 'pan' : 'orbit';
      // orbit about the point in the middle of the view (on the centre ray, as far as before, when there is none); pan at the cursor's depth
      const [cx, cy] = this.cam.centre, h = this.drag === 'orbit' ? this.pickOr(cx, cy) : this.pickOr(e.offsetX, e.offsetY);
      if (this.drag === 'orbit') this.pivot = h.hit || !this.pivot ? h.p : add(this.cam.eye, scale(this.cam.ray(cx, cy).d, dist(this.cam.eye, this.pivot)));
      this.dragDepth = this.depth;
    }
  }

  /** Lock the pointer for this drag (the browser may say no: the drag goes on unlocked). */
  private lockAsked = false;
  private lock() {
    this.lockAsked = true;
    try { (this.el.requestPointerLock() as unknown as Promise<void> | undefined)?.catch?.(() => {}); } catch { /* not allowed here */ }
  }

  private endPress() { if (this.press) { clearTimeout(this.press.timer); this.press = null; } }

  /** Held still: the pointer stops being a stroke or a drag (nor a tap when it lifts), and the menu opens. */
  private longPress(e: PointerEvent, x: number, y: number) {
    this.press = null;
    if (this.captured.delete(e.pointerId)) this.capture?.(e, 'cancel');
    this.ptrs.delete(e.pointerId);
    this.drag = null;
    this.onContext(x, y);
  }

  private move(e: PointerEvent) {
    if (this.keyStroke || (e.pointerType === 'mouse' && !e.buttons && !this.ptrs.size)) { this.hover(e); return; }
    if (this.press?.id === e.pointerId && Math.hypot(e.offsetX - this.press.x0, e.offsetY - this.press.y0) > 8) this.endPress();
    const cp = this.captured.get(e.pointerId);
    if (cp) { cp.x = e.offsetX; cp.y = e.offsetY; this.capture?.(e, 'move'); return; }
    const p = this.ptrs.get(e.pointerId);
    if (!p) return;
    // locked (an orbit drag), the pointer stays put and only says how far it moved
    const locked = e.pointerType === 'mouse' && document.pointerLockElement === this.el;
    const dx = locked ? e.movementX : e.offsetX - p.x, dy = locked ? e.movementY : e.offsetY - p.y;
    p.x = e.offsetX; p.y = e.offsetY;
    if (!dx && !dy) return;
    p.m += Math.hypot(dx, dy);
    if (this.drag === 'orbit' && this.lockOrbit && e.pointerType === 'mouse' && !locked && !this.lockAsked && p.m > 3 && this.ptrs.size === 1) this.lock();
    const c = this.cam;
    if (this.pinch && this.ptrs.size === 2) {
      const [a, b] = [...this.ptrs.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y), mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const k = this.pinch.d / Math.max(d, 1);
      if (c.mode === 'plan') { c.pan(mx - this.pinch.mx, my - this.pinch.my, 0); c.zoomPlan(k, mx, my); }
      else if (c.mode === 'orbit') {
        c.pan(mx - this.pinch.mx, my - this.pinch.my, this.depth);
        const t = this.pinch.anchor!;
        c.eye = add(t, scale(sub(c.eye, t), k));
      } else this.walkStep(0, (1 - k) * 3, 0);
      Object.assign(this.pinch, { d, mx, my });
      this.touched();
      return;
    }
    if (this.ptrs.size !== 1 || !this.drag) return;
    if (this.drag === 'orbit' && this.pivot) c.orbit(this.pivot, -dx * 0.006, -dy * 0.006);
    else if (this.drag === 'pan') c.pan(dx, dy, this.dragDepth);
    else if (this.drag === 'look' && document.pointerLockElement !== this.el) this.look(dx * 0.004, dy * 0.004);
    this.touched();
  }

  private up(e: PointerEvent, cancel = false) {
    if (this.keyStroke) return;
    if (this.press?.id === e.pointerId) this.endPress();
    if (this.captured.delete(e.pointerId)) { this.capture?.(e, cancel ? 'cancel' : 'up'); return; }
    const p = this.ptrs.get(e.pointerId);
    this.ptrs.delete(e.pointerId);
    if (this.ptrs.size < 2) this.pinch = null;
    if (!this.ptrs.size) {
      if (this.drag === 'orbit' && document.pointerLockElement === this.el) document.exitPointerLock();
      this.drag = null;
    }
    if (!p || cancel) return;
    const moved = Math.max(p.m, Math.hypot(e.offsetX - p.x0, e.offsetY - p.y0));
    if (e.button === 2) { if (moved < 6 && !this.ptrs.size) this.onContext(e.offsetX, e.offsetY); return; }   // a right-drag pans; a right-click is the menu
    if (moved < 6 && e.timeStamp - p.t0 < 400 && !this.ptrs.size && !this.onTap(e.offsetX, e.offsetY)) this.focus(e.offsetX, e.offsetY);
  }

  private wheel(e: WheelEvent) {
    e.preventDefault();
    const dy = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaY;
    const k = Math.exp(clamp(dy, -300, 300) * 0.0015);
    const c = this.cam, x = e.offsetX, y = e.offsetY;
    if (c.mode === 'plan') c.zoomPlan(k, x, y);
    else if (c.mode === 'walk') this.walkStep(0, -dy * 0.004, 0);
    else {
      const now = performance.now();
      if (!this.wheelHit || now - this.wheelHit.t > 250 || Math.hypot(x - this.wheelHit.x, y - this.wheelHit.y) > 4)
        this.wheelHit = { x, y, t: now, p: this.pickOr(x, y).p };
      this.wheelHit.t = now;
      const t = this.wheelHit.p!;
      const ne = add(t, scale(sub(c.eye, t), k));
      if (dist(ne, t) > 0.05) c.eye = ne;
      this.depth = clamp(dist(c.eye, t), 0.3, 200);
    }
    this.touched();
  }

  private look(dyaw: number, dpitch: number) {
    const c = this.cam;
    c.yaw += dyaw;
    c.pitch = clamp(c.pitch + dpitch, -1.5, 1.5);
    this.touched();
  }

  /** Move horizontally in the view's yaw: right, forward, up (metres). */
  walkStep(rt: number, fw: number, up: number) {
    const c = this.cam, f = forward(c.yaw, 0), r: V3 = [f[1], -f[0], 0];
    c.eye = add(c.eye, add(add(scale(f, fw), scale(r, rt)), [0, 0, up]));
  }

  /** A click/tap no tool used: orbit pivot to the point, move halfway towards it. */
  focus(x: number, y: number) {
    const h = this.v.pickAt(x, y);
    if (!h) return;
    const c = this.cam;
    this.pivot = h.p;
    if (c.mode === 'plan') { c.pc = [h.p[0], h.p[1]]; this.touched(); return; }
    if (c.mode === 'walk') return;
    const e1 = add(h.p, scale(sub(c.eye, h.p), 0.5));
    const d = norm(sub(h.p, e1));
    let y1 = Math.atan2(d[1], d[0]);
    while (y1 - c.yaw > Math.PI) y1 -= 2 * Math.PI;
    while (y1 - c.yaw < -Math.PI) y1 += 2 * Math.PI;
    this.anim = { t0: performance.now(), dur: 350, e0: c.eye, e1, y0: c.yaw, y1, p0: c.pitch, p1: Math.asin(clamp(d[2], -1, 1)) };
  }

  /** Per-frame: animation, keyboard and joystick movement. dt in seconds. */
  tick(dt: number) {
    const c = this.cam;
    if (this.anim) {
      const a = this.anim, t = clamp((performance.now() - a.t0) / a.dur, 0, 1), s = t * t * (3 - 2 * t);
      c.eye = add(a.e0, scale(sub(a.e1, a.e0), s));
      c.yaw = a.y0 + (a.y1 - a.y0) * s;
      c.pitch = a.p0 + (a.p1 - a.p0) * s;
      if (t >= 1) this.anim = null;
      this.touched();
    }
    const k = this.keys;
    if (!k.size && !this.joy[0] && !this.joy[1]) return;
    const sp = (k.has('ShiftLeft') || k.has('ShiftRight') ? 4.5 : 1.5) * dt;
    const ax = (k.has('KeyD') || k.has('ArrowRight') ? 1 : 0) - (k.has('KeyA') || k.has('ArrowLeft') ? 1 : 0) + this.joy[0];
    const ay = (k.has('KeyW') || k.has('ArrowUp') ? 1 : 0) - (k.has('KeyS') || k.has('ArrowDown') ? 1 : 0) - this.joy[1];
    const az = (k.has('KeyE') ? 1 : 0) - (k.has('KeyQ') ? 1 : 0);
    if (!ax && !ay && !az) return;
    if (c.mode === 'plan') c.pan(-ax * sp / c.ps * 2, ay * sp / c.ps * 2, 0);
    else this.walkStep(ax * sp, ay * sp, az * sp);
    this.touched();
  }
}

/**
 * Keep the camera's centre (where it looks and orbits about) on the middle of the pane showing of panes (the
 * View tab, or a see-through tab beside it), the canvas filling the page; the page's middle while none shows.
 * Follows the panes' resizes and the window's; returns the update, for the layout to call when it changes
 * shape (a pane moved, or another came to the front).
 */
export function followPane(v: Viewer, canvas: HTMLElement, ...panes: HTMLElement[]): () => void {
  const update = () => {
    const r = panes.map(p => p.getBoundingClientRect()).find(b => b.width && b.height), c = canvas.getBoundingClientRect(), cam = v.cam;
    const s: [number, number] = r ? [r.left + r.width / 2 - (c.left + c.width / 2), r.top + r.height / 2 - (c.top + c.height / 2)] : [0, 0];
    if (s[0] === cam.shift[0] && s[1] === cam.shift[1]) return;
    cam.shift = s;
    v.dirty = true;
  };
  for (const p of panes) new ResizeObserver(update).observe(p);
  addEventListener('resize', update);
  update();
  return update;
}
